import { http, ensureFreshToken, refreshTokens } from './http';
import { API_BASE_URL, ROUTES } from './config';
import { errorFromResponse } from './errors';
import { tokenStore } from './tokenStore';
import type { StoredImage } from '../types/domain';

/**
 * ImagesController client (manager+ upload/list/delete). Reads (serving the actual bytes)
 * are consumed directly as `<img src={url}>` — never through this client.
 *
 * The upload paths replicate productMedia.ts's auth handling: proactively refresh a stale
 * token before sending (ensureFreshToken — a token sitting unused in tokenStore does not
 * refresh itself just because time passes), plus a reactive refresh-and-retry-once on a 401
 * (token invalidated server-side despite looking fresh). Without this, a tab left open past
 * the 15-minute token lifetime 403s on upload while every JSON call keeps working — the exact
 * bug fixed in productMedia.ts on 2026-07-30 and mirrored here on 2026-09-04.
 */
export const imagesApi = {
  /** multipart/form-data — bypasses the JSON `http` client, which always sends JSON. */
  async upload(file: File, category?: string): Promise<StoredImage> {
    const form = new FormData();
    form.append('file', file);
    if (category) form.append('category', category);

    const response = await sendMultipart(`${ROUTES.images.base}`, form);
    if (!response.ok) {
      const problem = await response.json().catch(() => null);
      throw errorFromResponse(response.status, problem, response.headers.get('X-Correlation-Id'));
    }
    return response.json();
  },

  list(category?: string): Promise<StoredImage[]> {
    const q = category ? `?category=${encodeURIComponent(category)}` : '';
    return http.get(`${ROUTES.images.admin}${q}`);
  },

  /** Same upload as `upload()`, but over XHR so real byte-level progress can be reported —
   * `fetch` has no upload-progress event. Used by avatar/hero-image pickers that show a bar. */
  async uploadWithProgress(file: File, category: string | undefined, onProgress: (pct: number) => void): Promise<StoredImage> {
    const form = new FormData();
    form.append('file', file);
    if (category) form.append('category', category);

    return sendXhr(`${ROUTES.images.base}`, form, onProgress);
  },

  /** No replace endpoint exists server-side — callers delete then upload(). */
  remove(fileName: string): Promise<void> {
    return http.delete(`${ROUTES.images.base}/${fileName}`);
  },
};

async function sendMultipart(path: string, form: FormData, attempt = 0): Promise<Response> {
  const token = attempt === 0 ? await ensureFreshToken() : (await refreshTokens())?.accessToken;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  if (response.status === 401 && attempt === 0) return sendMultipart(path, form, attempt + 1);
  return response;
}

async function sendXhr(path: string, form: FormData, onProgress: (pct: number) => void, attempt = 0): Promise<StoredImage> {
  const token = attempt === 0 ? await ensureFreshToken() : (await refreshTokens())?.accessToken;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}${path}`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error('Upload succeeded but the response could not be read.'));
        }
      } else if (xhr.status === 401 && attempt === 0) {
        // Retry once with a freshly refreshed token — the file is small enough that
        // re-reading it from the FormData is cheaper than maintaining a state machine.
        sendXhr(path, form, onProgress, attempt + 1).then(resolve, reject);
      } else {
        let problem = null;
        try {
          problem = JSON.parse(xhr.responseText);
        } catch {
          /* non-JSON error body */
        }
        reject(errorFromResponse(xhr.status, problem, xhr.getResponseHeader('X-Correlation-Id')));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed. Please check your connection.'));
    xhr.send(form);
  });
}
